import axios from 'axios'

function generateRequestId() {
  return 'mv_req_' + Math.random().toString(36).slice(2, 9)
}

export function normalizeResponse(response) {
  if (!response || response.data === undefined) {
    return { data: response, meta: {}, links: {} }
  }

  const payload = response.data

  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) {
      return {
        data: payload.data,
        meta: payload.meta || {},
        links: payload.links || {},
      }
    }

    if (payload.data !== undefined) {
      return {
        data: payload.data,
        meta: payload.meta || {},
        links: payload.links || {},
      }
    }
  }

  return {
    data: payload,
    meta: {},
    links: {},
  }
}

const pendingRequests = new Map()
const responseCache = new Map()
const CACHE_TTL = {
  reference: 5 * 60 * 1000,
  analytics: 2 * 60 * 1000,
  dashboard: 1 * 60 * 1000,
  user: 30 * 60 * 1000,
}

const activeRequests = new Map()

function getCacheKey(config) {
  const method = (config.method || 'get').toUpperCase()
  const url = config.url
  const params = JSON.stringify(config.params || {})
  return `${method}:${url}:${params}`
}

function isCacheable(config) {
  const method = (config.method || 'get').toLowerCase()
  return method === 'get' && !config._noCache
}

function getCacheTTL(config) {
  const url = config.url || ''
  if (url.includes('/markets') || url.includes('/food-items') || url.includes('/locations')) {
    return CACHE_TTL.reference
  }
  if (url.includes('/analytics') || url.includes('/price-trends') || url.includes('/price-spread')) {
    return CACHE_TTL.analytics
  }
  if (url.includes('/dashboard')) {
    return CACHE_TTL.dashboard
  }
  if (url.includes('/user')) {
    return CACHE_TTL.user
  }
  return CACHE_TTL.reference
}

function getCachedResponse(key) {
  const entry = responseCache.get(key)
  if (!entry) return null

  const now = Date.now()
  if (now - entry.timestamp > entry.ttl) {
    responseCache.delete(key)
    return null
  }

  return entry.data
}

function setCachedResponse(key, data, ttl) {
  responseCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  })
}

function clearCacheForUrl(urlPattern) {
  for (const key of responseCache.keys()) {
    if (key.includes(urlPattern)) {
      responseCache.delete(key)
    }
  }
}

export function clearAllCache() {
  responseCache.clear()
  pendingRequests.clear()
}

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('va_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (!config.headers['X-Request-ID']) {
    config.headers['X-Request-ID'] = generateRequestId()
  }

  if (config.signal) {
    const requestId = config.headers['X-Request-ID']
    activeRequests.set(requestId, config.signal)
    config.signal.addEventListener('abort', () => {
      activeRequests.delete(requestId)
    })
  }

  return config
})

api.interceptors.request.use((config) => {
  if (isCacheable(config)) {
    const key = getCacheKey(config)
    const cached = getCachedResponse(key)
    if (cached) {
      config._cachedData = cached
    }
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    const config = response.config
    if (isCacheable(config)) {
      const key = getCacheKey(config)
      const ttl = getCacheTTL(config)
      const normalized = normalizeResponse(response)
      setCachedResponse(key, normalized.data, ttl)
    }
    const requestId = config?.headers?.['X-Request-ID']
    if (requestId) {
      activeRequests.delete(requestId)
    }
    return response
  },
  async (error) => {
    const status = error.response?.status
    const originalRequest = error.config

    const requestId = originalRequest?.headers?.['X-Request-ID']
    if (requestId) {
      activeRequests.delete(requestId)
    }

    if (status === 401 && !originalRequest?._retry) {
      if (originalRequest?._silent401) {
        return Promise.reject(error)
      }

      const responseData = error.response?.data
      if (responseData?.error === 'two_factor_required') {
        localStorage.setItem('mfa_pending', 'true')
        localStorage.setItem('mfa_temp_token', localStorage.getItem('token') || localStorage.getItem('va_token') || '')
        window.location.href = '/verify-2fa'
        return Promise.reject(error)
      }

      const publicPaths = ['/login', '/register']
      const isPublicPage = publicPaths.includes(window.location.pathname)
      const isMfaPending = localStorage.getItem('mfa_pending') === 'true'

      const refreshToken = localStorage.getItem('refresh_token')
      if (!isPublicPage && refreshToken && !isMfaPending) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          }).catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const { data } = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/refresh`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          )

          const newToken = data.token
          localStorage.setItem('token', newToken)
          localStorage.setItem('access_token', newToken)
          localStorage.setItem('va_token', newToken)
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token)
          }

          api.defaults.headers.common.Authorization = `Bearer ${newToken}`
          originalRequest.headers.Authorization = `Bearer ${newToken}`

          processQueue(null, newToken)
          return api(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('token')
          localStorage.removeItem('va_token')
          localStorage.removeItem('user')
          if (!isMfaPending) {
            window.location.href = '/login'
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      if (!isPublicPage) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('token')
        localStorage.removeItem('va_token')
        localStorage.removeItem('user')
        if (!isMfaPending) {
          window.location.href = '/login'
        }
      }

      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export function createAbortController() {
  const controller = new AbortController()
  return {
    signal: controller.signal,
    abort: () => controller.abort(),
  }
}

export function cancelAllRequests() {
  for (const [, signal] of activeRequests) {
    if (signal && !signal.aborted) {
    }
  }
  activeRequests.clear()
}

export function cancelRequest(requestId) {
  const signal = activeRequests.get(requestId)
  if (signal && !signal.aborted) {
  }
}

export function getActiveRequestIds() {
  return Array.from(activeRequests.keys())
}

export const apiWithSignal = {
  get: (url, config = {}) => api.get(url, config),
  post: (url, data, config = {}) => api.post(url, data, config),
  put: (url, data, config = {}) => api.put(url, data, config),
  patch: (url, data, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
}

export { clearCacheForUrl, getCacheKey, getCacheTTL }

export default api
